import React from 'react';
import { Pencil, Plus, Tag as TagIcon, Trash2, X } from 'lucide-react';
import ModalPortal from './ModalPortal';
import {
  getPositionDisplaySymbol,
  getPositionStrategies,
  getStrategyTagColor,
} from './positionsOrdersUtils';
import type { usePositionStrategyTags } from './usePositionStrategyTags';

type PositionStrategyTagState = ReturnType<typeof usePositionStrategyTags>;

interface PositionStrategyTagModalsProps {
  state: PositionStrategyTagState;
  strategyNameOptions: string[];
}

const PositionStrategyTagModals: React.FC<PositionStrategyTagModalsProps> = ({
  state,
  strategyNameOptions,
}) => {
  const {
    strategyTags,
    managerOpen,
    setManagerOpen,
    editorPosition,
    setEditorPosition,
    selectedTagIds,
    setSelectedTagIds,
    strategyLabelOverride,
    setStrategyLabelOverride,
    editingTagId,
    tagName,
    setTagName,
    tagDescription,
    setTagDescription,
    tagColor,
    setTagColor,
    tagBusy,
    tagError,
    openManagerFromEditor,
    resetTagForm,
    editTag,
    saveStrategyTag,
    removeStrategyTag,
    savePositionTags,
  } = state;

  const automaticStrategyLabels = editorPosition
    ? getPositionStrategies(editorPosition).filter((strategy) => strategy !== 'UNASSIGNED')
    : [];

  return (
    <>
      {managerOpen && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
            onClick={(event) => {
              if (event.target === event.currentTarget) setManagerOpen(false);
            }}
          >
            <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0e0e12] shadow-[0_0_50px_rgba(0,0,0,0.5)]">
              <div className="flex items-start justify-between border-b border-white/10 px-6 py-5">
                <div>
                  <div className="flex items-center gap-2 text-neon">
                    <TagIcon className="h-4 w-4" />
                    <h3 className="text-lg font-bold text-white">Position labels</h3>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted">
                    Create reusable notes such as “earnings hedge” or “review later”.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setManagerOpen(false)}
                  className="rounded-lg p-2 text-white/45 transition-colors hover:bg-white/5 hover:text-white"
                  aria-label="Close label manager"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-5 overflow-y-auto p-6">
                {tagError && (
                  <div className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                    {tagError}
                  </div>
                )}
                <form onSubmit={saveStrategyTag} className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-white/75">
                      {editingTagId ? 'Edit label' : 'New label'}
                    </h4>
                    {editingTagId && (
                      <button
                        type="button"
                        onClick={resetTagForm}
                        className="text-[10px] font-bold uppercase tracking-wider text-white/45 hover:text-white"
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                    <label className="space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted">Name</span>
                      <input
                        value={tagName}
                        onChange={(event) => setTagName(event.target.value)}
                        maxLength={80}
                        placeholder="e.g. Earnings hedge"
                        className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-neon/50"
                        required
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted">Color</span>
                      <input
                        type="color"
                        value={tagColor}
                        onChange={(event) => setTagColor(event.target.value.toUpperCase())}
                        className="block h-[38px] w-full cursor-pointer rounded-lg border border-white/10 bg-black/20 p-1"
                        aria-label="Label color"
                      />
                    </label>
                  </div>
                  <label className="block space-y-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted">Description (optional)</span>
                    <textarea
                      value={tagDescription}
                      onChange={(event) => setTagDescription(event.target.value)}
                      maxLength={500}
                      rows={2}
                      placeholder="Short explanation shown when hovering the tag"
                      className="w-full resize-none rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-neon/50"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={tagBusy || !tagName.trim()}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-neon/30 bg-neon/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-neon transition-colors hover:bg-neon/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Plus className="h-3 w-3" />
                    {editingTagId ? 'Save changes' : 'Add label'}
                  </button>
                </form>

                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-white/55">Your labels</h4>
                  {strategyTags.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-white/10 px-4 py-5 text-center text-xs text-muted">
                      No position labels yet.
                    </p>
                  ) : strategyTags.map((tag) => (
                    <div key={tag.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: getStrategyTagColor(tag) }} />
                          <span className="truncate text-sm font-bold text-white">{tag.name}</span>
                        </div>
                        {tag.description && <p className="mt-1 truncate text-xs text-muted">{tag.description}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => editTag(tag)}
                          disabled={tagBusy}
                          className="rounded-lg p-2 text-white/45 transition-colors hover:bg-white/5 hover:text-neon disabled:opacity-40"
                          aria-label={`Edit ${tag.name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeStrategyTag(tag)}
                          disabled={tagBusy}
                          className="rounded-lg p-2 text-white/45 transition-colors hover:bg-rose-500/10 hover:text-rose-300 disabled:opacity-40"
                          aria-label={`Delete ${tag.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {editorPosition && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
            onClick={(event) => {
              if (event.target === event.currentTarget) setEditorPosition(null);
            }}
          >
            <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#0e0e12] p-6 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-neon">
                    <TagIcon className="h-4 w-4" />
                    <h3 className="text-lg font-bold text-white">Position labels</h3>
                  </div>
                  <p className="mt-1 text-xs text-muted">{getPositionDisplaySymbol(editorPosition)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditorPosition(null)}
                  className="rounded-lg p-2 text-white/45 transition-colors hover:bg-white/5 hover:text-white"
                  aria-label="Close position labels"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {tagError && (
                <div className="mb-4 rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                  {tagError}
                </div>
              )}
              <section className="mb-5 rounded-xl border border-neon/20 bg-neon/[0.04] p-4" aria-labelledby="automatic-position-strategies">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 id="automatic-position-strategies" className="text-[10px] font-bold uppercase tracking-wider text-white/70">
                    Strategy label
                  </h4>
                  <span className="rounded border border-neon/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-neon/70">
                    Automatic attribution
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {automaticStrategyLabels.length > 0 ? automaticStrategyLabels.map((strategy) => (
                    <span key={strategy} className="rounded border border-neon/25 bg-neon/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-neon">
                      {strategy}
                    </span>
                  )) : (
                    <span className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] font-semibold text-white/45">
                      No automatic strategy label
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[10px] leading-relaxed text-muted">
                  This attribution identifies which strategy opened the position. Set a display name below without changing order ownership or automatic closes.
                </p>
                <label htmlFor="custom-position-strategy" className="mt-4 block text-[10px] font-bold uppercase tracking-wider text-white/70">
                  Display name
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="custom-position-strategy"
                    type="text"
                    list="position-strategy-name-options"
                    value={strategyLabelOverride}
                    onChange={(event) => setStrategyLabelOverride(event.target.value)}
                    maxLength={80}
                    placeholder={automaticStrategyLabels.join(' + ') || 'Name this strategy label'}
                    className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/25 outline-none transition-colors focus:border-neon/50"
                  />
                  {strategyLabelOverride.trim() && (
                    <button
                      type="button"
                      onClick={() => setStrategyLabelOverride('')}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-rose-400/20 px-2.5 py-2 text-[10px] font-bold uppercase tracking-wider text-rose-200/75 transition-colors hover:bg-rose-400/10 hover:text-rose-100"
                    >
                      <X className="h-3 w-3" />
                      Use automatic
                    </button>
                  )}
                </div>
                <datalist id="position-strategy-name-options">
                  {strategyNameOptions.map((name) => <option key={name} value={name} />)}
                </datalist>
                <button
                  type="button"
                  onClick={() => setStrategyLabelOverride('UNASSIGNED')}
                  className="mt-2 rounded-md border border-white/10 px-2 py-1 text-[10px] font-semibold text-white/60 hover:border-rose-400/30 hover:text-white"
                >
                  Remove strategy name
                </button>
                {strategyNameOptions.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 text-[10px] uppercase tracking-wider text-white/40">Strategy names:</span>
                    {strategyNameOptions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setStrategyLabelOverride(name)}
                        className={`rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors ${strategyLabelOverride.trim() === name
                          ? 'border-neon/40 bg-neon/10 text-neon'
                          : 'border-white/10 text-white/55 hover:border-neon/30 hover:text-neon'
                          }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-[10px] leading-relaxed text-muted">
                  Leave blank to show the automatic name, or remove the name for this position. This changes the displayed label only.
                </p>
              </section>

              <section className="space-y-2" aria-labelledby="position-description-tags">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 id="position-description-tags" className="text-[10px] font-bold uppercase tracking-wider text-white/65">
                      Descriptive labels
                    </h4>
                    <p className="mt-0.5 text-[10px] text-muted">Notes and reminders attached to this position.</p>
                  </div>
                  {selectedTagIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedTagIds([])}
                      className="text-[10px] font-bold uppercase tracking-wider text-white/45 transition-colors hover:text-rose-300"
                    >
                      Remove all labels
                    </button>
                  )}
                </div>
                {strategyTags.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/10 px-4 py-5 text-center text-xs text-muted">
                    Create a position label first, then attach it here.
                  </div>
                ) : strategyTags.map((tag) => {
                  const checked = selectedTagIds.includes(tag.id);
                  return (
                    <div key={tag.id} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition-colors ${checked ? 'border-neon/30 bg-neon/10' : 'border-white/10 bg-white/[0.03]'}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-bold text-white">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: getStrategyTagColor(tag) }} />
                          <span className="truncate">{tag.name}</span>
                          {checked && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-neon">Attached</span>}
                        </div>
                        {tag.description && <p className="mt-0.5 truncate text-xs text-muted">{tag.description}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedTagIds((ids) => checked ? ids.filter((id) => id !== tag.id) : [...ids, tag.id])}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${checked
                          ? 'border-rose-400/20 text-rose-200/75 hover:bg-rose-400/10 hover:text-rose-100'
                          : 'border-neon/20 text-neon/80 hover:bg-neon/10 hover:text-neon'
                          }`}
                        aria-label={`${checked ? 'Remove' : 'Add'} ${tag.name} ${checked ? 'from' : 'to'} position`}
                      >
                        {checked ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                        {checked ? 'Remove' : 'Add'}
                      </button>
                    </div>
                  );
                })}
              </section>
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={openManagerFromEditor}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white/60 transition-colors hover:border-neon/30 hover:text-neon"
                >
                  <Plus className="h-3 w-3" />
                  Manage labels
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditorPosition(null)}
                    className="rounded-lg border border-white/10 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={savePositionTags}
                    disabled={tagBusy}
                    className="rounded-lg border border-neon/30 bg-neon/10 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-neon transition-colors hover:bg-neon/20 disabled:opacity-40"
                  >
                    {tagBusy ? 'Saving…' : 'Save labels'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
};

export default PositionStrategyTagModals;
